import { useEffect, useMemo, useState } from "react";
import { useHeaderAction } from "../../../../core/provider/HeaderActionProvider/HeaderAction.js";
import { Button } from "../../../../components/ui/Button/index.js";
import { toastify } from "../../../../components/ui/Toasts/index.js";
import {
  FileSpreadsheet,
  Download,
  RefreshCw,
  ChevronRight,
  ChevronLeft,
  Loader2,
  FileX2,
  MoreHorizontal,
} from "lucide-react";
import { cn } from "../../../../lib/cn.js";
import { useGetExcelFileList } from "../../../../core/api/admins/customer/queris.js";
import { useDownloadExcelFile } from "../../../../core/api/admins/customer/mutation.js";

// ============================================
// ✅ helpers
// ============================================
const fmtFa = (v: any) => new Intl.NumberFormat("fa-IR").format(Number(v ?? 0));

const formatSize = (bytes: number) => {
  if (!bytes) return "-";
  if (bytes < 1024) return `${fmtFa(bytes)} بایت`;
  if (bytes < 1024 * 1024) return `${fmtFa((bytes / 1024).toFixed(1))} KB`;
  return `${fmtFa((bytes / 1024 / 1024).toFixed(1))} MB`;
};

const PAGE_SIZE = 10;

// ============================================
// ✅ تولید آرایه شماره صفحات با ellipsis
//    مثال: [1, "...", 4, 5, 6, "...", 12]
// ============================================
const buildPageNumbers = (
  currentPage: number, // 0-based
  totalPages: number,
  maxVisible = 5
): (number | string)[] => {
  const pages: (number | string)[] = [];
  const current = currentPage + 1; // 1-based

  if (totalPages <= maxVisible) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
    return pages;
  }

  const start = Math.max(1, current - 2);
  const end = Math.min(totalPages, start + maxVisible - 1);

  if (start > 1) {
    pages.push(1);
    if (start > 2) pages.push("...");
  }

  for (let i = start; i <= end; i++) pages.push(i);

  if (end < totalPages) {
    if (end < totalPages - 1) pages.push("...");
    pages.push(totalPages);
  }

  return pages;
};

// ============================================
// ✅ کامپوننت اصلی
// ============================================
export default function ExcelFileListPage() {
  const { setAction } = useHeaderAction();
  const [page, setPage] = useState(0);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const {
    data: listResponse,
    isLoading,
    isFetching,
    refetch,
  } = useGetExcelFileList({
    page,
    size: PAGE_SIZE,
    sortField: "DECS",
    sortDirection: "DECS",
  });

  const { mutate: downloadFile } = useDownloadExcelFile();

  const files = listResponse?.payload || [];
  const count = listResponse?.count || 0;
  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  // ============================================
  // ✅ شماره صفحات (با ellipsis)
  // ============================================
  const pageNumbers = useMemo(
    () => buildPageNumbers(page, totalPages, 5),
    [page, totalPages]
  );

  const goToPage = (p: number) => {
    if (p < 0 || p >= totalPages) return;
    setPage(p);
  };

  // ============================================
  // ✅ هدر
  // ============================================
  useEffect(() => {
    setAction(
      <div className="flex items-center justify-between w-full h-full">
        <div className="flex items-center gap-2.5">
          <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-brand-50 text-brand-600">
            <FileSpreadsheet className="w-4 h-4" />
          </div>
          <h1 className="text-sm font-bold text-neutral-900">
            لیست فایل‌های اکسل
          </h1>
        </div>

        <Button
          variant="outline"
          color="neutral"
          size="sm"
          rounded="xl"
          leftIcon={
            <RefreshCw
              className={cn("w-3.5 h-3.5", isFetching && "animate-spin")}
            />
          }
          onClick={() => refetch()}
          disabled={isFetching}
          className="!h-9 !text-[11px]"
        >
          بروزرسانی
        </Button>
      </div>
    );
    return () => setAction(null);
  }, [isFetching, setAction, refetch]);

  // ============================================
  // ✅ دانلود فایل
  // ============================================
  const handleDownload = (file: any) => {
    setDownloadingId(file.id);
    downloadFile(file.id, {
      onSuccess: (blobData: Blob) => {
        const ext = file.fileExtension === "EXCEL" ? "xlsx" : "xls";
        const url = window.URL.createObjectURL(
          new Blob([blobData], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          })
        );
        const link = document.createElement("a");
        link.href = url;
        link.download = `${file.fileName || "file"}.${ext}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toastify("success", "فایل دانلود شد");
      },
      onError: () => toastify("error", "خطا در دانلود فایل"),
      onSettled: () => setDownloadingId(null),
    });
  };

  // ============================================
  // ✅ اطلاعات بازه نمایشی
  // ============================================
  const startItem = count === 0 ? 0 : page * PAGE_SIZE + 1;
  const endItem = Math.min((page + 1) * PAGE_SIZE, count);

  // ============================================
  // ✅ رندر
  // ============================================
  return (
    <div className="flex flex-col h-full -m-3">
      <div className="flex-1 min-h-0 p-3 overflow-auto space-y-3">
        {/* ===== کارت جدول ===== */}
        <div className="bg-white border rounded-2xl border-neutral-200 overflow-hidden">
          {/* هدر کارت */}
          <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-neutral-100">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-brand-50 text-brand-600">
                <FileSpreadsheet className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-[13px] font-bold text-neutral-900">
                  فایل‌های آپلود شده
                </h3>
                <p className="text-[10px] text-neutral-500 mt-0.5">
                  {isLoading ? "..." : `${fmtFa(count)} فایل`}
                </p>
              </div>
            </div>

            {isFetching && !isLoading && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-brand-50 text-brand-700 text-[10px] font-semibold">
                <Loader2 className="w-3 h-3 animate-spin" />
                در حال بروزرسانی
              </div>
            )}
          </div>

          {/* محتوا */}
          {isLoading ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16">
              <Loader2 className="w-6 h-6 text-brand-500 animate-spin" />
              <p className="text-[11px] text-neutral-400">در حال بارگذاری...</p>
            </div>
          ) : files.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16">
              <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-neutral-50">
                <FileX2 className="w-6 h-6 text-neutral-300" />
              </div>
              <p className="text-[12px] text-neutral-500">
                هنوز فایلی آپلود نشده است
              </p>
            </div>
          ) : (
            <>
              {/* جدول */}
              <div className="overflow-x-auto">
                <table className="w-full text-right">
                  <thead>
                    <tr className="border-b border-neutral-100 bg-neutral-50/50">
                      <th className="px-4 py-3 text-[11px] font-bold text-neutral-500 w-12">
                        #
                      </th>
                      <th className="px-4 py-3 text-[11px] font-bold text-neutral-500">
                        نام فایل
                      </th>
                      <th className="px-4 py-3 text-[11px] font-bold text-neutral-500">
                        تاریخ آپلود
                      </th>
                      <th className="px-4 py-3 text-[11px] font-bold text-neutral-500">
                        دسته‌بندی
                      </th>
                      <th className="px-4 py-3 text-[11px] font-bold text-neutral-500">
                        حجم
                      </th>
                      <th className="px-4 py-3 text-[11px] font-bold text-neutral-500">
                        نوع
                      </th>
                      <th className="px-4 py-3 text-[11px] font-bold text-neutral-500 w-24">
                        عملیات
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {files.map((file, idx) => {
                      const isDownloading = downloadingId === file.id;
                      return (
                        <tr
                          key={file.id}
                          className="border-b border-neutral-50 hover:bg-neutral-50/40 transition-colors"
                        >
                          <td className="px-4 py-3 text-[11px] text-neutral-400 tabular-nums">
                            {fmtFa(page * PAGE_SIZE + idx + 1)}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-emerald-50 text-emerald-600 shrink-0">
                                <FileSpreadsheet className="w-3.5 h-3.5" />
                              </div>
                              <span className="text-[12px] font-semibold text-neutral-800 truncate max-w-[220px]">
                                {file.fileName}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-[11px] text-neutral-600 tabular-nums">
                            {file.uploadDate || "-"}
                          </td>
                          <td className="px-4 py-3">
                            {file.category ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-brand-50 text-brand-700 border border-brand-100">
                                {file.category}
                              </span>
                            ) : (
                              <span className="text-[11px] text-neutral-300">
                                -
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-[11px] text-neutral-600 tabular-nums">
                            {formatSize(file.size)}
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-violet-50 text-violet-700 border border-violet-100">
                              {file.fileExtension}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <Button
                              variant="soft"
                              color="primary"
                              size="sm"
                              rounded="lg"
                              className="!h-8 !text-[10px] !px-3"
                              leftIcon={<Download className="w-3 h-3" />}
                              onClick={() => handleDownload(file)}
                              loading={isDownloading}
                              disabled={downloadingId !== null}
                            >
                              {isDownloading ? "..." : "دانلود"}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* ============================================ */}
              {/* ✅ Pagination حرفه‌ای */}
              {/* ============================================ */}
              {totalPages > 1 && (
                <div className="flex flex-col items-center justify-between gap-3 px-4 py-3 border-t border-neutral-100 sm:flex-row">
                  {/* اطلاعات بازه */}
                  <div className="flex items-center gap-1.5 text-[11px] text-neutral-500">
                    <span className="font-semibold text-neutral-700 tabular-nums">
                      {fmtFa(startItem)}
                    </span>
                    <span>تا</span>
                    <span className="font-semibold text-neutral-700 tabular-nums">
                      {fmtFa(endItem)}
                    </span>
                    <span>از</span>
                    <span className="font-semibold text-neutral-700 tabular-nums">
                      {fmtFa(count)}
                    </span>
                    <span>فایل</span>
                  </div>

                  {/* دکمه‌ها */}
                  <div className="flex items-center gap-1">
                    {/* قبلی */}
                    <button
                      onClick={() => goToPage(page - 1)}
                      disabled={page === 0 || isFetching}
                      className={cn(
                        "flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200",
                        "border border-neutral-200 bg-white text-neutral-600",
                        "hover:bg-neutral-50 hover:border-neutral-300 hover:text-neutral-800",
                        "disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-neutral-200"
                      )}
                      aria-label="صفحه قبل"
                    >
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>

                    {/* شماره صفحات */}
                    {pageNumbers.map((pageNum, idx) => {
                      if (pageNum === "...") {
                        return (
                          <span
                            key={`ellipsis-${idx}`}
                            className="flex items-center justify-center w-8 h-8 text-neutral-400"
                          >
                            <MoreHorizontal className="w-3.5 h-3.5" />
                          </span>
                        );
                      }

                      const num = Number(pageNum);
                      const isActive = num === page + 1;

                      return (
                        <button
                          key={num}
                          onClick={() => goToPage(num - 1)}
                          disabled={isFetching}
                          className={cn(
                            "flex items-center justify-center min-w-8 h-8 px-2 text-[11px] font-bold rounded-lg transition-all duration-200 tabular-nums",
                            isActive
                              ? "bg-brand-500 text-white shadow-sm shadow-brand-500/30"
                              : "text-neutral-600 hover:bg-neutral-100 hover:text-neutral-800",
                            isFetching && "opacity-50 cursor-not-allowed"
                          )}
                        >
                          {fmtFa(num)}
                        </button>
                      );
                    })}

                    {/* بعدی */}
                    <button
                      onClick={() => goToPage(page + 1)}
                      disabled={page >= totalPages - 1 || isFetching}
                      className={cn(
                        "flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200",
                        "border border-neutral-200 bg-white text-neutral-600",
                        "hover:bg-neutral-50 hover:border-neutral-300 hover:text-neutral-800",
                        "disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-neutral-200"
                      )}
                      aria-label="صفحه بعد"
                    >
                      <ChevronLeft className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}